"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getCurrentUser, listWorkspaces, getSearchBackendStatus } from "@/lib/api";
import type { Workspace } from "@/lib/mockData";

export interface AppUser {
  id: string;
  email: string;
  full_name?: string;
  role?: string;
  firm_id?: string;
  title?: string;
  practice_area?: string;
  subscription_plan?: string;
  [key: string]: any;
}

interface AppContextType {
  user: AppUser | null;
  workspaces: Workspace[];
  backendOnline: boolean | null;
  loading: boolean;
  refetchWorkspaces: () => Promise<void>;
  refetchUser: () => Promise<AppUser | null>;
  refetchBackendStatus: () => Promise<void>;
}

const AppContext = createContext<AppContextType>({
  user: null,
  workspaces: [],
  backendOnline: null,
  loading: true,
  refetchWorkspaces: async () => {},
  refetchUser: async () => null,
  refetchBackendStatus: async () => {},
});

export const useAppContext = () => useContext(AppContext);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [backendOnline, setBackendOnline] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async (): Promise<AppUser | null> => {
    try {
      const u = await getCurrentUser();
      setUser(u || null);
      return u || null;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const ws = await listWorkspaces();
      setWorkspaces(ws);
    } catch {
      setWorkspaces([]);
    }
  }, []);

  const fetchBackendStatus = useCallback(async () => {
    try {
      const status = await getSearchBackendStatus();
      setBackendOnline(status.online ?? false);
    } catch {
      setBackendOnline(false);
    }
  }, []);

  // Initial parallel prefetch on mount
  useEffect(() => {
    let isMounted = true;
    async function prefetchAll() {
      try {
        const [fetchedUser] = await Promise.all([
          fetchUser(),
          fetchWorkspaces(),
          fetchBackendStatus(),
        ]);
        if (!fetchedUser && isMounted) setUser(null);
      } catch (err) {
        console.error("[AppProvider] Prefetch error:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    prefetchAll();
    return () => { isMounted = false; };
  }, [fetchUser, fetchWorkspaces, fetchBackendStatus]);

  // Background silent refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => {
      if (user) {
        fetchWorkspaces();
        fetchBackendStatus();
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [user, fetchWorkspaces, fetchBackendStatus]);

  return (
    <AppContext.Provider value={{ user, workspaces, backendOnline, loading, refetchWorkspaces: fetchWorkspaces, refetchUser: fetchUser, refetchBackendStatus: fetchBackendStatus }}>
      {children}
    </AppContext.Provider>
  );
}

export default AppContext;
